import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { SmallContainer } from 'app/components/common/container';
import { Paper } from 'app/components/common/paper';
import { RescheduleTechnicianMapper } from 'app/page/orders/reschedule-technician/reschedule-technician-mapper';
import { RootActions, RootState } from 'app/redux';
import {
    fetchAvailableTimeActionCreator,
    initializeScheduleAppointmentActionCreator,
    submitScheduleAppointmentActionCreator,
} from 'app/page/orders/redux/orders-action-creators';
import { PendingAction } from 'app/page/orders/redux/model/pending-action';
import { PendingActionUtils } from 'app/page/orders/utils/pending-action-utils';
import { PendingType } from 'app/page/orders/redux/model/pending-type';
import { ActiveOrder } from 'app/page/orders/model/active-order';
import { OrderUtils } from 'app/page/orders/utils/order-utils';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { BillingAccountUtils } from 'app/page/orders/utils/billing-account-utils';
import { FormName } from 'app/page/orders/form-name';

import { RescheduleTechnicianForm } from './reschedule-technician-form';
import { RescheduleTechnicianFormPlaceholder } from './reschedule-technician-form-placeholder';
import { AvailableTimeProvider } from './available-time-provider';

interface StateProps {
    billingAccounts: BillingAccount[];
    selectedBillingAccountId?: string;
    pendingActions: PendingAction[];
}

interface DispatchProps {
    initializeScheduleAppointment: typeof initializeScheduleAppointmentActionCreator;
    fetchAvailableTime: typeof fetchAvailableTimeActionCreator;
    submitScheduleAppointment: typeof submitScheduleAppointmentActionCreator;
}

type Props = StateProps & DispatchProps;

const RescheduleTechnicianPageComponent = (props: Props): React.ReactElement<Props> => {
    const {
        billingAccounts,
        selectedBillingAccountId,
        pendingActions,
        initializeScheduleAppointment,
        fetchAvailableTime,
        submitScheduleAppointment,
    } = props;

    if (!selectedBillingAccountId) {
        throw new Error('Billing account must be selected.');
    }

    const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
        billingAccounts,
        selectedBillingAccountId,
    );
    const { customerInformation, activeOrders, selectedOrderId } = billingAccount;

    if (!customerInformation) {
        throw new Error('Customer information must be present.');
    }

    if (!selectedOrderId) {
        throw new Error('Order must be selected.');
    }

    const activeOrder: ActiveOrder = activeOrders[selectedOrderId];
    const appointment: OrderTrackerBackend.Appointment = OrderUtils.getAppointment(activeOrder);
    const partnerId: string = customerInformation.partnerId;
    const cartId: string = OrderUtils.getShoppingCart(activeOrder).id;
    const availableTimeProvider: AvailableTimeProvider = React.useMemo(() => new AvailableTimeProvider(), []);

    const handleSubmit = React.useCallback(
        (values) => {
            submitScheduleAppointment(RescheduleTechnicianMapper.mapAppointment(values));
        },
        [submitScheduleAppointment],
    );

    const handleCalendarDayVisit = React.useCallback(
        (day) => {
            if (availableTimeProvider.needsFetch(partnerId, cartId, day)) {
                fetchAvailableTime(day, availableTimeProvider);
            }
        },
        [cartId, partnerId, availableTimeProvider, fetchAvailableTime],
    );

    React.useEffect(() => {
        initializeScheduleAppointment(availableTimeProvider);
    }, [cartId, partnerId, availableTimeProvider, initializeScheduleAppointment]);

    const pendingInitialization: boolean = PendingActionUtils.isActionPending(
        pendingActions,
        PendingType.INITIALIZE_SCHEDULE_APPOINTMENT,
    );

    return (
        <SmallContainer>
            <Paper>
                {pendingInitialization ? (
                    <RescheduleTechnicianFormPlaceholder />
                ) : (
                    <RescheduleTechnicianForm
                        form={FormName.RESCHEDULE_TECHNICIAN}
                        initialValues={RescheduleTechnicianMapper.mapAppointmentForm(appointment)}
                        onSubmit={handleSubmit}
                        onCalendarDayVisit={handleCalendarDayVisit}
                    />
                )}
            </Paper>
        </SmallContainer>
    );
};

const mapStateToProps = (state: RootState): StateProps => {
    const {
        orders: { billingAccounts, selectedBillingAccountId, pendingActions },
    } = state;

    return {
        pendingActions,
        billingAccounts,
        selectedBillingAccountId,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootActions>): DispatchProps => ({
    ...bindActionCreators(
        {
            initializeScheduleAppointment: initializeScheduleAppointmentActionCreator,
            fetchAvailableTime: fetchAvailableTimeActionCreator,
            submitScheduleAppointment: submitScheduleAppointmentActionCreator,
        },
        dispatch,
    ),
});

const RescheduleTechnicianPage = connect(mapStateToProps, mapDispatchToProps)(RescheduleTechnicianPageComponent);

export { RescheduleTechnicianPage };
